import { useQuery } from "@apollo/client";
import { useMemo } from "react";

import useBlobStorageFlag from "hooks/useBlobStorageFlag";
import useOriginFlag from "hooks/useOriginFlag";

import { GET_INTEGRATIONS_COUNT, IntegrationsCountData } from "../gql";
import { getIntegrationById, integrationIds } from "../registry";
import { IntegrationId } from "../registry/constants";

const INTEGRATION_ID_TO_COUNT_KEY: Record<
  IntegrationId,
  keyof IntegrationsCountData["integrationsCount"] | undefined
> = {
  [IntegrationId.AWS]: "aws",
  [IntegrationId.AZURE]: "azure",
  [IntegrationId.AZURE_DEVOPS]: "azureDevOps",
  [IntegrationId.BACKSTAGE]: "backstage",
  [IntegrationId.BITBUCKET_CLOUD]: "bitbucketCloud",
  [IntegrationId.BITBUCKET_DATA_CENTER]: "bitbucketDatacenter",
  [IntegrationId.BLOB_STORAGE]: "blobStorage",
  [IntegrationId.GITHUB]: "github",
  [IntegrationId.GITLAB]: "gitlab",
  [IntegrationId.ORIGIN]: "origin",
  [IntegrationId.SERVICENOW]: "serviceNow",
  [IntegrationId.SLACK]: "slack",
  [IntegrationId.VCS_AGENT_POOLS]: "vcsAgentPools",
  [IntegrationId.WEBHOOKS]: "webhooks",
  [IntegrationId.AI]: "ai",
  [IntegrationId.DATADOG]: undefined,
  [IntegrationId.GCP]: undefined,
  [IntegrationId.GRAPHQL_API]: undefined,
  [IntegrationId.SPACECTL]: undefined,
  [IntegrationId.TEAMS]: undefined,
  [IntegrationId.TERRAFORM_PROVIDER]: undefined,
};

export const useIntegrations = () => {
  const blobStorageEnabled = useBlobStorageFlag();
  const originEnabled = useOriginFlag();
  const { data, loading, error } = useQuery(GET_INTEGRATIONS_COUNT);

  const integrations = useMemo(() => {
    // TODO: remove this filter together with the vcs-provider-blob-storage feature flag.
    const integrations = integrationIds
      .map((id) => getIntegrationById(id))
      .filter((integration) => integration !== undefined)
      .filter((integration) => integration.id !== IntegrationId.BLOB_STORAGE || blobStorageEnabled)
      .filter((integration) => integration.id !== IntegrationId.ORIGIN || originEnabled);

    if (!data) {
      return integrations;
    }

    // populate the count for the registry integrations
    return integrations.map((integration) => {
      const countKey = INTEGRATION_ID_TO_COUNT_KEY[integration.id];
      if (!countKey) return integration;

      return {
        ...integration,
        count: data.integrationsCount[countKey],
      };
    });
  }, [data, blobStorageEnabled, originEnabled]);

  return {
    integrations,
    loading,
    error,
  };
};
